import React, { useMemo } from "react";
import { ModalV2, Button, Text, Flex } from "@jsq/ds/core";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { useComplianceEntityOnboardingEnabled } from "../../../flags";
import { isInternalEscalationSource } from "../../../utils/constants";
import * as styles from "./styles_vanilla.css";

export type CreateEscalationsNoteModalProps = {
  show: boolean;
  onHide: () => void;
  onContinue: () => void;
  escalationsCount?: number;
  escalations?: NonNullable<
    NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
  >;
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
};

const formatEscalationText = (count: number, word: string = "escalation"): string => {
  const pluralRules = new Intl.PluralRules("en-US");
  const pluralForm = pluralRules.select(count);
  return `${count} ${word}${pluralForm === "one" ? "" : "s"}`;
};

export const CreateEscalationsNoteModal: React.FC<CreateEscalationsNoteModalProps> = ({
  show,
  onHide,
  onContinue,
  escalationsCount,
  escalations,
  profilesForCase,
}) => {
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const additionalEscalations = useMemo(() => {
    if (internalEscalationsEnabled && isInternalEscalationSource(profilesForCase?.caseType)) {
      return 0;
    }
    if (!escalations || !profilesForCase) return escalationsCount ?? 0;
    const currentCaseId = profilesForCase.id;
    const uniqueProfileIds = Array.from(
      new Set(
        escalations
          .map((e) => (e?.profileId ? Number(e.profileId) : null))
          .filter((id): id is number => typeof id === "number"),
      ),
    );

    return uniqueProfileIds.reduce((total, profileId) => {
      const profile =
        profilesForCase.profiles?.find((p) => p?.id === profileId) ||
        (profilesForCase.investingProfile?.id === profileId
          ? profilesForCase.investingProfile
          : null);

      if (!profile?.complianceCases) return total;

      const additionalCasesCount = profile.complianceCases.filter(
        (caseItem) => caseItem?.complianceCase?.id !== currentCaseId,
      ).length;

      return total + additionalCasesCount;
    }, 0);
  }, [internalEscalationsEnabled, escalations, profilesForCase, escalationsCount]);

  return (
    <ModalV2
      show={show}
      onHide={onHide}
      backdrop={true}
      closeOnClickOutside={false}
      contentClassName={styles.modal}
      title="Note"
    >
      <ModalV2.Body>
        <Text>
          {`${formatEscalationText(escalationsCount ?? 0)} will be created and assigned to you.`}
          {additionalEscalations > 0 && (
            <>
              <br />
              <br />
              {`${formatEscalationText(additionalEscalations, "additional escalation")} will be created for clients where the escalated profiles have active positions. These escalations can be worked on by any user.`}
            </>
          )}
        </Text>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex gap="md" justifyContent="flex-end">
          <Button onClick={onHide} variant="neutral">
            Cancel
          </Button>
          <Button onClick={onContinue} variant="primary" color="green">
            Continue
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
